import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { Layout } from './components/Layout';
import { AnimatePresence, motion } from 'motion/react';
import { buildListingPath } from './lib/routes';
import { isPublicDirectoryTherapySlug } from './lib/publicTherapies';
import { ListingPage } from './pages/ListingPage';

const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const VenueDetailPage = lazy(() => import('./pages/VenueDetailPage').then(m => ({ default: m.VenueDetailPage })));
const JournalPage = lazy(() => import('./pages/JournalPage').then(m => ({ default: m.JournalPage })));
const ArticlePage = lazy(() => import('./pages/ArticlePage').then(m => ({ default: m.ArticlePage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ClaimPage = lazy(() => import('./pages/ClaimPage').then(m => ({ default: m.ClaimPage })));
const EditorialPage = lazy(() => import('./pages/EditorialPage').then(m => ({ default: m.EditorialPage })));
const ProgrammaticPage = lazy(() => import('./pages/ProgrammaticPage').then(m => ({ default: m.ProgrammaticPage })));
const GA_MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ?? '';

const PageWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

const RouteAnalytics = () => {
  const location = useLocation();

  React.useEffect(() => {
    const pagePath = `${location.pathname}${location.search}${location.hash}`;

    if (window.gtag && GA_MEASUREMENT_ID) {
      window.gtag('config', GA_MEASUREMENT_ID, { page_path: pagePath });
    }

    if (window.hj) {
      window.hj('stateChange', pagePath);
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const LegacyBestRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/${slug}` : '/journal'} replace />;
};

const RETIRED_EDITORIAL_REDIRECTS: Record<string, string> = {
  'best-cryotherapy-london': '/best-recovery-centres-london',
};

const RetiredEditorialRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  const destination = slug ? RETIRED_EDITORIAL_REDIRECTS[slug] : null;

  if (!destination) {
    return <EditorialPage />;
  }

  return <Navigate to={destination} replace />;
};

const ListingRoute = () => {
  const { city, therapy } = useParams<{ city: string; therapy: string }>();
  const normalizedTherapy = therapy?.trim().toLowerCase() ?? 'all';

  if (normalizedTherapy !== 'all' && !isPublicDirectoryTherapySlug(normalizedTherapy)) {
    return <Navigate to={buildListingPath(city, 'all')} replace />;
  }

  return <ListingPage />;
};

export default function App() {
  return (
    <Router>
      <RouteAnalytics />
      <Layout>
        <Suspense fallback={<div className="min-h-screen" />}>
        <PageWrapper>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/directory" element={<ListingPage />} />
            <Route path="/all/all" element={<Navigate to="/directory" replace />} />
            <Route path="/all" element={<Navigate to="/directory" replace />} />
            <Route path="/:city/:therapy" element={<ListingRoute />} />
            <Route path="/:city/:therapy/:venue" element={<VenueDetailPage />} />
            
            {/* Fallback routes for categories */}
            <Route path="/cities" element={<Navigate to="/directory" replace />} />
            <Route path="/events" element={<Navigate to="/journal" replace />} />
            <Route path="/deals" element={<Navigate to="/directory" replace />} />
            <Route path="/news" element={<Navigate to="/journal" replace />} />
            <Route path="/goals/*" element={<Navigate to="/directory" replace />} />
            <Route path="/guides" element={<Navigate to="/journal" replace />} />
            <Route path="/best" element={<Navigate to="/journal" replace />} />
            <Route path="/best/:slug" element={<LegacyBestRedirect />} />
            <Route path="/best-cryotherapy-london" element={<Navigate to="/best-recovery-centres-london" replace />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/journal/:id" element={<ArticlePage />} />
            <Route path="/discover" element={<Navigate to="/directory" replace />} />
            <Route path="/discover/:slug" element={<ProgrammaticPage />} />
            <Route path="/find" element={<Navigate to="/directory" replace />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/claim" element={<Navigate to="/submit-venue" replace />} />
            <Route path="/submit-venue" element={<ClaimPage />} />
            <Route path="/newsletter" element={<Navigate to="/#newsletter" replace />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sauna-ice-bath-lowdown" element={<Navigate to="/journal" replace />} />
            <Route path="/:slug" element={<RetiredEditorialRedirect />} />
          </Routes>
        </PageWrapper>
        </Suspense>
      </Layout>
    </Router>
  );
}
